import { Grid, Icon, IconButton, Typography } from '@material-ui/core';
import { DateRange, HighlightOff, Refresh } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useRef, useState } from 'react';
import { Auto, FormApi, formApi, Tags, worksheetTypeForm } from '.';


import {
  AgeInQueue,
  WorksheetIcon
} from '../components/coding-list/coding-status';
import { VisitListApi } from '../components/coding-list/visit-list-api';
import { tabData } from '../components/coding-list/visit-page-activity';
import { PeriodSearch } from '../components/common/forms';
import { OrganizationSelect } from '../components/common/organization-select';
import {
  AuditBatch,
  auditBatch,
  AuditBatchApi,
  auditOrganizationCode,
  setAuditWorksheet
} from './audit-batch-api';
import { AuditDetails, AuditWorksheet } from './audit-report-api';
import { AuditScoreCard } from './audit-report-card';
import { dateRangeForm, formatDate, formatLocalDate } from './date-time';
import { documentResolver } from './document-resolver';
import { Visit } from './domain';
import { Expansion, ExpansionDetails, ExpansionSummary } from './expansion';
import { personName, withTooltip } from './format';
import { IconMenu } from './icon-menu';
import { Spinner } from './loading';
import { organizationApi, OrganizationSelection } from './organization-api';
import {
  createPageApi,
  PagedTable,
  TableColumn,
  TableProps
} from './paged-table';
import {
  chartOwnerLastField, locationField,
  mrnField,
  ownerLastField,
  patientLastField,
  serviceDateField, visitAccountField, visitFirstDateInCodingField,
  visitInsuranceField, worksheetQueue,
  worksheetStateField
} from './visit-queue';

const auditBatchColumns: TableColumn<AuditBatch>[] = [
  {
    label: '',
    render: (r) => (
      <IconMenu
        icon="menu"
        onClick={(a) => console.log('menu', a)}
        menus={[
          { label: 'Disable Audit Batch' },
          { label: 'Enable Audit Batch' },
        ]}
        htmlColor={'#aaa'}
      />
    ),
    style: { width: 36, align: 'center' },
  },

  {
    label: 'Description',
    sort: false,
    id: 'description',
    style: { width: '25vw' },
  },
  {
    label: 'Selection Method',
    sort: true,
    id: 'selectionMethod',
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Start Date',
    sort: true,
    render: (r) => r.startDate && formatDate(r.startDate),
    style: { width: 80 },
  },
  {
    label: 'End Date',
    sort: false,
    render: (r) => formatDate(r.endDate),
    style: { width: 80 },
  },
  {
    label: 'Current Worksheets',
    sort: false,
    render: (r) => r.currentWorksheets,
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Maximum Worksheets',
    sort: false,
    render: (r) => r.maximumWorksheets,
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Worksheets Returned To Coder',
    sort: false,
    render: (r) => r.worksheetsReturnedToCoder,
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Audited Worksheets',
    sort: false,
    render: (r) => r.auditedWorksheets,
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Skipped Worksheets',
    sort: false,
    render: (r) => r.skippedWorksheets,
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Active ',
    sort: false,
    id: 'active',
    render: (r) => (r.active ? 'Yes' : 'No'),
    style: { width: 50, align: 'center' },
  },
  {
    label: 'Location',
    sort: false,
    id: 'locationCode',
    style: { width: '60px' },
  },
  {
    label: 'Department',
    sort: false,
    id: 'departmentCode',
    style: { width: '60px' },
  },
  {
    render: (r) => {
      return (
        <BatchLink
          tooltip="View Audit Report Card"
          onClick={() => {
            auditBatch.actions.startBatch(r);
            auditBatch.actions.getSummary(r.auditId);
          }}
          icon="bar_chart"
        />
      );
    },
    style: { width: 24, padding: 'none' },
  },
  {
    render: (r) => {
      return (
        <BatchLink
          tooltip="Start Coding Batch"
          onClick={() => auditBatch.actions.startBatch(r)}
          icon="play_circle_filled"
        />
      );
    },
    style: { width: 24, padding: 'none' },
  },
];

function BatchLink({ icon, onClick, tooltip }) {
  return withTooltip(
    tooltip,
    <IconButton color="primary" onClick={onClick}>
      <Icon>{icon}</Icon>
    </IconButton>
  );
}

const defaultTable: Partial<TableProps<AuditBatch>> = {
  columns: auditBatchColumns,
  pageApi: createPageApi(),
};

type AuditBatchTableProps = {
  selection: OrganizationSelection;
  searchResults: any;
  table?: TableProps<AuditBatch>;
  api?: AuditBatchApi;
  range?: any,
  org?: any,
  onSearch?: (values: PeriodSearch) => void;
  apiProps?: VisitListApi;
};

export function AuditBatchTable(props: AuditBatchTableProps) {
  const { api } = { ...props };

  const batches = api.data((s) => s.batches);
  const totalResults = batches?.totalResults || 0;
  const rows = batches?.entries || [];
  const table = { ...defaultTable, ...props.table };
  const selected = api.data((s) => s.batch);

  const rowIsSelected = (r: AuditBatch) =>
    selected && selected.auditId === r.auditId;

  const onRequestPage = (a) => {
    api.actions.requestPage(a);
  };
  const onRequestSort = (order: number, a: TableColumn) => {
    api.actions.requestSort(order, a);
  };

  return (
    <PagedTable
      {...table}
      rows={rows}
      totalResults={totalResults}
      containerWidth="98vw"
      rowIsSelected={rowIsSelected}
    />
  );
}

function BatchDescription() {
  const batch = auditBatch.data((s) => s.batch);
  const showBatch = batch;

  const onClearAuditBatch = () => {
    auditBatch.actions.clearBatch();
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={0}
      style={{ width: '100%' }}
    >
      <Grid item>
        <Typography
          style={{ whiteSpace: 'nowrap' }}
          variant="body2"
          color="textPrimary"
        >
          Audit Batches
        </Typography>
      </Grid>
      {showBatch && (
        <>
          <Grid item style={{ flexGrow: 1 }}></Grid>
          <Grid item style={{ paddingRight: 12 }}>
            <Typography
              style={{ whiteSpace: 'nowrap' }}
              variant="body2"
              color="textSecondary"
            >
              Selected Audit Batch
            </Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">
              {batch.description}
            </Typography>
          </Grid>
          <Grid item>
            {withTooltip(
              'Clear Audit Batch',
              <IconButton onClick={onClearAuditBatch}>
                <HighlightOff />
              </IconButton>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
}

function AuditBatches(props: AuditBatchTableProps) {
  const { api } = { ...props };
  const [expanded, setExpanded] = useState(true);
  const [clicked, setClicked] = useState(false);

  const batch = api.data((s) => s.batch);
  const summary = api.data((s) => s.summary);

  useEffect(() => {
    if (batch || summary) {
      setExpanded(false);
      setClicked(false);
    }
  }, [batch, summary]);

  const onClicked = () => {
    setExpanded(!expanded);
    setClicked(true);
  };

  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon />}>
        <BatchDescription />
      </ExpansionSummary>
      <ExpansionDetails>
        <AuditBatchTable {...props} />
      </ExpansionDetails>
    </Expansion>
  );
}

function AuditReportCard(props: AuditBatchTableProps) {
  const { api } = props;
  const [expanded, setExpanded] = useState(true);
  const onClicked = () => {
    setExpanded(!expanded);
  };
  const summary = api.data((s) => s.summary);
  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon />}>
        Audit Report Card
      </ExpansionSummary>
      <ExpansionDetails>
        <AuditScoreCard summary={summary} api={api} />
      </ExpansionDetails>
    </Expansion>
  );
}

const readonlyUrl = ({ locationCode, departmentCode, visitId }: Visit) =>
  `${window.location.origin
  }/visit?vis=${visitId}&loc=${locationCode}&dep=${departmentCode}&org=${auditOrganizationCode()}&wor=auditStd`;

export function openReadonlyVisit(a: Visit) {
  worksheetQueue.setState({ visit: a });
  window.open(readonlyUrl(a), '_blank');
}

type ReadOnlyAccountLinkProps = Visit & {
  icon?: string;
  onClick?: (a?: any) => void;
};

export function ReadOnlyAccountLink(props: ReadOnlyAccountLinkProps) {
  const { onClick, icon } = props;
  return (
    <a
      onClick={onClick}
      href={readonlyUrl(props)}
      target={'_blank'}
      style={{ textDecoration: 'none' }}
    >
      <IconButton color="primary">
        <Icon>{icon}</Icon>
      </IconButton>
    </a>
  );
}

export const worksheetColumns: TableColumn<AuditWorksheet>[] = [
  {
    label: '',
    render: () => '',
    style: { width: '12px' },
  },
  {
    label: 'Account',
    id: 'accountNumber',
    sort: true,
    style: { width: 70 },
    data: visitAccountField,
  },

  {
    label: 'DOS',
    sort: true,
    render: (r) => formatLocalDate(r.serviceDate),
    style: { width: 100 },
    data: serviceDateField,
  },
  {
    label: 'Patient',
    sort: true,
    render: (r) => r.patientName,
    style: { width: '15vw' },
    data: { fieldName: 'patient.lastName' },
  },
  {
    label: 'Coder',
    sort: true,
    render: (r) => r.coderName,
    style: { width: '15vw' },
    data: { fieldName: 'workflowOwners[0].lastName' },
  },
  {
    label: 'Provider',
    sort: true,
    render: (r) => r.physicianName,
    style: { width: '15vw' },
    data: { fieldName: 'owningProvider.lastName' },
  },
  {
    label: 'Total Possible',
    sort: true,
    render: (r) => r.totalPossiblePoints,
    style: { width: 90 },
    data: { fieldName: 'totalPossiblePoints' },
  },
  {
    label: 'Score',
    sort: true,
    render: (r) => r.totalActualPoints,
    style: { width: 90 },
    data: { fieldName: 'totalActualPoints' },
  },
  {
    label: 'ICD',
    sort: true,
    render: (r) => r.totalICDPoints,
    style: { width: 90 },
    data: { fieldName: 'totalICDPoints' },
  },
  {
    label: 'CPT',
    sort: true,
    render: (r) => r.totalCPTPoints,
    style: { width: 90 },
    data: { fieldName: 'totalCPTPoints' },
  },
  {
    label: 'Location',
    id: 'locationCode',
    sort: true,
    style: { width: 90 },
    data: locationField,
  },
  {
    label: 'Department',
    id: 'departmentCode',
    sort: true,
    style: { width: 90 },
    data: { fieldName: 'departmentCode' },
  },
  {
    render: (r) => {
      return (
        <ReadOnlyAccountLink
          onClick={setAuditWorksheet}
          visitId={r.visitId}
          locationCode={r.locationCode}
          departmentCode={r.departmentCode}
          icon="play_circle_filled"
        />
      );
    },
    style: { width: 24, padding: 'none' },
  },
];

export function AuditWorksheetTable(props: AuditBatchTableProps) {
  const { api } = props;

  const result: AuditDetails = api.data((s) => s.details);
  const rows = result?.worksheets || [];
  const totalResults = (result && result.totalRecords) || 0;
  const selected = api.data((s) => s.selected);

  const [expanded, setExpanded] = useState(!!result);

  const onClicked = () => {
    setExpanded(!expanded);
  };
  const rowIsSelected = (r: any) =>
    selected && selected.worksheetId === r.worksheetId;

  const onRowClick = (r: any) => {
    api.data.setState({ selected: r });
  };
  const onRequestPage = (a) => {
    api.actions.requestPage(a);
  };
  const onRequestSort = (order: number, a: TableColumn) => {
    api.actions.requestSort(order, a);
  };

  useEffect(() => {
    setExpanded(!!rows?.length);
  }, [result]);

  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon />}>
        Audit Drilldown - {api.data.getState().drillDownTitle}
      </ExpansionSummary>
      <ExpansionDetails>
        <PagedTable
          containerWidth={'98vw'}
          containerHeight={'44vh'}
          onRowClick={onRowClick}
          columns={worksheetColumns}
          rows={rows}
          rowIsSelected={rowIsSelected}
          totalResults={totalResults}
          pageApi={api.pageApi}
          onRequestPage={onRequestPage}
          onRequestSort={onRequestSort}
        />
      </ExpansionDetails>
    </Expansion>
  );
}

export function AuditBatchPage(props: AuditBatchTableProps) {
  const { api, selection, searchResults } = { ...props };
  const summary = api.data((s) => s.summary);
  const details = api.data((s) => s.details);


  return (
    <div
      style={{
        width: '100%',
        height: '85vh',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      {/*{selection?.location && <AuditBatches {...props} />}
      <br />

       {summary ? (
        <AuditReportCard {...props} />
      ) : (
        <>
          No Audit Summary Available
          <br />
        </>
      )}

      {details && (
        <>
          <br />
          <AuditWorksheetTable {...props} />
        </>
      )} */}

      {searchResults}

    </div>
  );
}

export const coderAuditColumns: TableColumn<Visit>[] = [
  {
    label: 'Age',
    sort: true,
    render: (r) => AgeInQueue(r.firstDateInCoding),
    style: { width: 40, padding: 'default', align: 'center' },
    data: visitFirstDateInCodingField,
  },
  {
    label: 'State',
    sort: true,
    render: (r) => WorksheetIcon(r.wsSummaries[0]),
    style: { width: 90 },
    data: worksheetStateField,
  },
  {
    label: 'Owner',
    sort: true,
    render: (r) => personName(r.owner),
    style: { width: 90 },
    data: chartOwnerLastField,
  },
  {
    label: 'Account',
    id: 'accountNumber',
    sort: true,
    style: { width: 90 },
    data: visitAccountField,
  },
  {
    label: 'MRN',
    id: 'mrn',
    sort: true,
    style: { width: 90 },
    data: mrnField,
  },
  {
    label: 'DOS',
    sort: true,
    render: (r) => formatLocalDate(r.serviceDate),
    style: { width: 100 },
    data: serviceDateField,
  },
  {
    label: 'Patient',
    sort: true,
    render: (r) => personName(r.patient),
    style: { width: '15vw' },
    data: patientLastField,
  },
  {
    label: 'Provider',
    sort: true,
    render: (r) => personName(r.wsSummaries[0]?.owningProvider),
    style: { width: '15vw' },
    data: ownerLastField,
  },
  {
    label: 'Insurance',
    sort: true,
    render: (r) => (r.insurances && r.insurances[0]?.companyName) || '' || '',
    style: { width: '17vw' },
    data: visitInsuranceField,
  },

  {
    render: (row) => (
      <IconButton
        color="primary"
        onClick={() => documentResolver.openWindow(row)}
      >
        <Icon>description</Icon>
      </IconButton>
    ),

    style: { width: 24, padding: 'none' },
  },
  {
    render: (r) => {
      return (
        <ReadOnlyAccountLink
          onClick={() => worksheetQueue.setState({ visit: r })}
          visitId={r.visitId}
          locationCode={r.locationCode}
          departmentCode={r.departmentCode}
          icon="play_circle_filled"
        />
      );
    },
    style: { width: 24, padding: 'none' },
  },
];

export function AuditBatchPageRoute() {
  const data = organizationApi((s) => s.organizations)?.length > 0;
  return (
    <Spinner complete={organizationApi && data}>
      <></>
    </Spinner>
  );
}

export function AuditView(props: AuditBatchTableProps) {
  return (
    <div></div>
  )
}

export function AuditReviewPage(props: AuditBatchTableProps) {
  const { api, selection, searchResults, org, onSearch, apiProps } = { ...props };
  const summary = api.data((s) => s.summary);
  const details = api.data((s) => s.details);

  const batches = api.data((s) => s.batches)



  const form: FormApi<{ batchControl: any, providerControl: any, coderControl: any }> = useRef(formApi({
    values: {
      batchControl: [],
      providerControl: [],
      coderControl: []
    },
    fields: {
      batchControl: {
        required: true,
        list: [],
        listLabel: (v) => v.label,
        listValue: (v) => v.name,
        style: { width: undefined },
        onChange: (v) => {
        },
        onEnter: () => { console.log('entered') }
      },
      providerControl: {
        required: true,
        list: [],
        listLabel: (v) => v.label,
        style: { width: undefined },
        onChange: (v) => {
        },
        onEnter: () => { console.log('entered') }
      },
      coderControl: {
        required: true,
        list: [],
        listLabel: (v) => v.label,
        style: { width: undefined },
        onChange: (v) => {
        },
        onEnter: () => { console.log('entered') }
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      console.log('V ', v)
    },
  })).current;

  const { batchControl, providerControl, coderControl } = form.controls;
  batchControl.variant = "outlined";
  providerControl.variant = "outlined";
  coderControl.variant = "outlined";



  useEffect(() => {

    let temp = [];
    batches?.entries.map(item => {
      temp.push({ label: item.auditId, name: item.auditId })
    })
    batchControl.list.setState({ value: temp })


  }, [batches])

  useEffect(() => {
    auditBatch.actions.refreshBatchList(org as any);
  }, [])



  const period = useRef(
    dateRangeForm({
      label: 'Period',
      onChange: (v) =>
        onSearch({
          period: v ? v.range : null,
        }),
    })
  ).current;

  const { range } = period.controls;
  range.variant = 'outlined';

  const worksheetType = useRef(worksheetTypeForm({
    label: 'Worksheet Type',
    onchange: (v) => ({
      worksheetType: v ? v.range : null
    }),
    items: [{ label: 'PHYSICIAN', name: 'PHYSICIAN' }, { label: 'FACILITY', name: 'FACILITY' }]
  })
  ).current;

  let rangeWorksheet = worksheetType.controls.range;
  rangeWorksheet.variant = 'outlined'
  const onRefresh = () => {
    auditBatch.actions.refreshBatchList(org as any);
  }


  console.log(' SUMMARY ', summary);


  return (
    <div
    >

      <Grid style={{ width: '70%', flexWrap: 'nowrap', display: 'flex', flexDirection: 'row', paddingBottom: 10 }}>
        <Grid item container direction='row'
          style={{ flexWrap: 'nowrap', paddingBottom: 6, paddingTop: 6, width: '100%' }}
          alignItems='flex-start'>
          <Grid item>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Date range:</div>
            {/* <RangePicker {...range} /> */}
          </Grid>
          <Grid item style={{ paddingLeft: 15, paddingRight: 15 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Worksheet Type:</div>
            <Auto {...rangeWorksheet} />
          </Grid>
        </Grid>
        <OrganizationSelect
          api={org as any}
          direction="horizontal"
          controlWidth="100%"
          partial={true}
          variant="outlined"
        />
        <IconButton onClick={onRefresh} style={{ width: '12px', height: '12px', top: 32 }}>
          {
            withTooltip('Refresh', <Refresh color='primary' fontSize='large' style={{ paddingTop: 4 }} />)
          }
        </IconButton>
      </Grid>
      <Grid item container direction='column'
        spacing={2}
        style={{ width: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}
        alignItems='flex-start'
        justify='space-between'>
        <div style={{ paddingLeft: 10, display: 'flex', flexDirection: 'row' }}>
          <DateRange color="disabled" />
          <div style={{ fontSize: 14, marginLeft: 10 }}>{range?.value.getState()?.value && formatDate(range.value.getState().value?.range?.startDate) + ' - ' + formatDate(range.value.getState().value?.range?.endDate)}</div>
        </div>

      </Grid>

      <Grid style={{ width: '70%', flexWrap: 'nowrap', overflow: 'hidden', display: 'flex', flexDirection: 'row', paddingBottom: 7, paddingTop: 15 }}>
        <Grid item container direction='row'
          style={{ flexWrap: 'nowrap', paddingBottom: 6, paddingTop: 6, width: '100%' }}
          alignItems='flex-start'>
          <Grid item style={{ flex: 1, paddingRight: 15 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Audit Batches:</div>
            <Tags {...batchControl} />
          </Grid>
          <Grid item style={{ flex: 1, paddingLeft: 15, paddingRight: 15 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Providers:</div>

            <Tags {...providerControl} />
          </Grid>
          {tabData.getState().tab === 0 && <Grid item style={{ flex: 1, paddingLeft: 15, paddingRight: 15 }}>
            <div style={{ color: 'gray', paddingBottom: 5, paddingLeft: 5 }}>Coders:</div>

            <Tags {...coderControl} />
          </Grid>}
        </Grid>
      </Grid>
      {/* <AuditScoreCard summary={summary} api={api}  /> */}


      <br />




      {selection?.location && <AuditBatches {...props} />}
      <br />

      {summary ? (
        <AuditReportCard {...props} />
      ) : (
        <>
          No Audit Summary Available
          <br />
        </>
      )}

      {details && (
        <>
          <br />
          <AuditWorksheetTable {...props} />
        </>
      )}
      {!details && (
        <>
          <br />
          Audit Visit List
          <br />
          {searchResults}
        </>
      )}
    </div>
  );
}


